import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, parse, startOfWeek, addDays, isSameDay } from 'date-fns';
import { Calendar, CalendarClock, Link2, List, Users, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Tooltip, TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent } from '../ui/Tooltip';
import {
  buildAcademyOccurrencesFromLessonSessions,
  enrichLessonOccurrenceClickPayloadFully,
  lessonCalendarOccurrenceToClickPayload,
  scheduleOccurrenceToClickPayload,
  type LessonOccurrenceClickPayload,
  type MakeupSessionRef,
  type ReportedAbsenceSessionRef,
  type ScheduleOccurrence,
  type ScheduleOccurrenceSourceType,
  type SchedulePendingBookingRef,
  type ScheduleEnrollmentBookingRef,
  type RemovedFromSessionRef,
  type ScheduleActiveStatusFilter,
} from '@/utils/scheduleOccurrences';
import {
  appendSchedulePastOpacity,
  isScheduleOccurrencePast,
} from '@/utils/lessonSlotColors';
import { getScheduleOccurrenceChipProps } from '@/utils/scheduleOccurrenceChipProps';
import { AgeGroupCalendarLegend, ScheduleTypeStatusLegend } from '@/components/sports/AgeGroupCalendarLegend';
import { CalendarChipSportGlyph } from '@/components/sport/CalendarChipSportGlyph';
import type { AcademySport } from '@court-sync/shared';
import { useAgeGroupLookups } from '@/queries/ageGroups';
import { formatTimeHHMM, formatTime, timeToMinutes } from '@/utils/timeFormat';
import type { Coach, Lesson, LessonOverride, LessonSession, Term } from '@court-sync/shared';
import type { LessonAvailabilityWarningDays } from '@/utils/coachAvailabilityWarnings';
import { LessonCalendarView } from '@/components/schedule/LessonCalendarView';
import { ScheduleFiltersAndCount, ScheduleWeekNav } from '@/components/schedule/ScheduleToolbar';
import { ScheduleCurrentTimeIndicator } from '@/components/schedule/ScheduleCurrentTimeIndicator';
import { useScheduleScrollToNow } from '@/hooks/useScheduleScrollToNow';
import { ensureTimeRangeIncludesNow } from '@/utils/scheduleScroll';
import { getStackedTimeSlotStyle } from '@/utils/scheduleOverlapLayout';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ScheduleCardToolbar({
  leading,
  trailing,
}: {
  leading: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 items-center gap-1 sm:gap-2">{leading}</div>
      {trailing ? (
        <div className="flex w-full min-w-0 items-center gap-2 sm:ml-auto sm:w-auto sm:justify-end sm:gap-3">
          {trailing}
        </div>
      ) : null}
    </div>
  );
}

function ScheduleListShell({
  header,
  title,
  toolbarTrailing,
  children,
}: {
  header?: React.ReactNode;
  title: string;
  toolbarTrailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full">
      {header}
      <Card className="overflow-hidden">
        <CardHeader className="mb-0 flex w-full flex-col gap-3 p-3 pb-3 sm:mb-4 sm:p-6 sm:pb-4">
          <ScheduleCardToolbar
            leading={<h3 className="shrink-0 text-base font-semibold sm:text-lg">{title}</h3>}
            trailing={toolbarTrailing}
          />
        </CardHeader>
        <CardContent className="px-3 pb-3 pt-0 sm:px-6 sm:pb-6">{children}</CardContent>
      </Card>
    </div>
  );
}

/** Narrow time gutter + seven day columns (replaces equal `grid-cols-8`). */
const WEEKLY_GRID_CLASS = 'grid grid-cols-[2.75rem_repeat(7,minmax(6.75rem,1fr))]';

/** Vertical pixels per 30-minute row in the weekly time grid (taller = larger cells). */
const WEEKLY_PX_PER_30_MIN = 50;

const PREVIEW_STACK_ID = '__preview__';

function scheduleOccurrenceStackKey(occ: ScheduleOccurrence): string {
  return `${occ.lessonId}-${occ.date.getTime()}`;
}

export interface PreviewLesson {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  name?: string;
}

export interface AcademyScheduleCalendarProps {
  lessons: Lesson[];
  terms: Term[];
  overrides: LessonOverride[];
  coaches?: Coach[];
  /** Clicking a weekly/monthly occurrence opens session details (replaces lesson-only click) */
  onLessonOccurrenceClick?: (payload: LessonOccurrenceClickPayload) => void;
  /** Clicking court hire / closure / play event chips opens schedule detail */
  onScheduleOccurrenceClick?: (occurrence: ScheduleOccurrence) => void;
  coachAvailabilityWarnings?: LessonAvailabilityWarningDays;
  defaultView?: 'monthly' | 'weekly';
  /** Controlled schedule view - when set, parent controls view and internal toggle is hidden */
  scheduleView?: 'monthly' | 'weekly';
  onScheduleViewChange?: (view: 'monthly' | 'weekly') => void;
  /** Preview lesson from create form (shown in weekly view) */
  previewLesson?: PreviewLesson;
  /** Full header mode: view toggles (list/monthly/weekly), filters, lesson count, create button */
  viewMode?: 'list' | 'monthly' | 'weekly';
  onViewModeChange?: (mode: 'list' | 'monthly' | 'weekly') => void;
  /** Filter popover content (rendered inside Filters button) */
  filterSlot?: React.ReactNode;
  hasActiveFilters?: boolean;
  /** Lesson count for header */
  lessonCount?: { filtered: number; total: number };
  /** Show Create Lesson button (academy owner dashboard only) */
  showCreateLesson?: boolean;
  onCreateLesson?: (prefillDate?: Date) => void;
  createLessonTooltip?: string;
  createLessonDisabled?: boolean;
  /** @deprecated Day cells open the day lessons popover; use onCreateLesson from the header instead */
  onCalendarDayClick?: (date: Date) => void;
  /** Content when viewMode is 'list' */
  listContent?: React.ReactNode;
  /** Content when loading */
  loading?: boolean;
  /** Content when empty (no lessons or no match). With full header, only applies in list view; monthly/weekly show the calendar grid empty. */
  emptyContent?: React.ReactNode;
  /** Hide the header - use when parent renders it separately */
  hideHeader?: boolean;
  /** Controlled month for monthly view (optional) */
  currentMonth?: Date;
  onCurrentMonthChange?: (date: Date) => void;
  /** Controlled week start for weekly view (optional) */
  weekStart?: Date;
  onWeekStartChange?: (weekStart: Date) => void;
  /** Called when the user navigates to a new month (monthly view) */
  onMonthChange?: (year: number, month: number) => void;
  /** Called when the user navigates to a new week (weekly view) */
  onWeekChange?: (weekStart: Date) => void;
  /** Sessions where a student used a make-up credit (weekly grid indicator) */
  makeupSessions?: MakeupSessionRef[];
  /** Pending bookings for per-occurrence unconfirmed badges (recommended; falls back to lesson.pendingEnrollment if omitted) */
  pendingScheduleBookings?: SchedulePendingBookingRef[];
  /** Confirmed/pending roster bookings for per-session enrollment counts on calendar chips */
  scheduleEnrollmentBookings?: ScheduleEnrollmentBookingRef[];
  /** Students removed from a single session (attendance cancelled) */
  removedFromSessionEnrollments?: RemovedFromSessionRef[];
  /** Reported absences / missed attendance per session (weekly grid indicator) */
  reportedAbsenceSessions?: ReportedAbsenceSessionRef[];
  /** Materialized sessions for this calendar range (merges sessionId and authoritative status/times). */
  lessonSessions?: LessonSession[];
  /**
   * When set (coach “My Schedule”), occurrences are limited to sessions whose resolved coach roster includes this coach domain id — so per-session substitutions hide the lesson from coaches who were replaced for that date only.
   */
  scheduleForCoachId?: string;
  /** Weekly grid: show copy booking link in hover tooltip for private lessons (owner list view parity). */
  onCopyPrivateLessonLink?: (lessonId: string, lessonName: string) => void;
  /** Court id → name for labeling occurrences */
  courtNamesById?: Record<string, string>;
  /** Renders on the right side of the schedule toolbar (with Monthly/Weekly toggles), same row as week/month navigation where applicable */
  scheduleToolbarEnd?: React.ReactNode;
  /** Optional row below the schedule toolbar (e.g. filter hints), above the calendar grid */
  scheduleToolbarBelow?: React.ReactNode;
  /** Colour / type legend rendered below the calendar grid */
  scheduleLegend?: React.ReactNode;
  /** Program id → display name for calendar chip tooltips */
  programNameById?: Record<string, string>;
  /** Owner lesson calendar: when not `active`, include inactive lessons/camps per status filter */
  scheduleActiveStatusFilter?: ScheduleActiveStatusFilter;
  /** When the academy runs multiple sports, show a sport glyph on calendar chips */
  showSportGlyphsOnChips?: boolean;
  academySports?: readonly AcademySport[];
  /** Weekly grid column headers: calendar dates (default) or weekday names only (planning surfaces). */
  weeklyColumnHeaders?: 'calendar' | 'weekday';
  /** Court hire / closure occurrences merged into monthly and weekly grids. */
  extraOccurrences?: ScheduleOccurrence[];
  /** When set, only occurrences with these source types are shown. */
  sourceTypeFilters?: ScheduleOccurrenceSourceType[];
  /** Academy id for age-group lookups when lessons array is empty (club-only calendar). */
  academyIdForAgeGroups?: string;
  /** Show current-time line and scroll-to-now on operational calendar views. */
  showCurrentTimeIndicator?: boolean;
}

export function AcademyScheduleCalendar({
  lessons,
  terms,
  overrides,
  defaultView,
  coaches = [],
  onLessonOccurrenceClick,
  onScheduleOccurrenceClick,
  coachAvailabilityWarnings = new Map(),
  scheduleView: controlledScheduleView,
  onScheduleViewChange,
  previewLesson,
  viewMode,
  onViewModeChange,
  filterSlot,
  hasActiveFilters: _hasActiveFilters,
  lessonCount,
  showCreateLesson = false,
  onCreateLesson,
  createLessonTooltip,
  createLessonDisabled = false,
  listContent,
  loading = false,
  emptyContent,
  hideHeader = false,
  currentMonth,
  onCurrentMonthChange,
  weekStart: controlledWeekStart,
  onWeekStartChange,
  onMonthChange,
  onWeekChange,
  makeupSessions = [],
  pendingScheduleBookings,
  scheduleEnrollmentBookings,
  removedFromSessionEnrollments,
  reportedAbsenceSessions = [],
  lessonSessions,
  scheduleForCoachId,
  onCopyPrivateLessonLink,
  courtNamesById,
  scheduleToolbarEnd,
  scheduleToolbarBelow,
  scheduleLegend,
  programNameById,
  scheduleActiveStatusFilter = 'active',
  showSportGlyphsOnChips = false,
  academySports = [],
  weeklyColumnHeaders = 'calendar',
  extraOccurrences = [],
  sourceTypeFilters,
  academyIdForAgeGroups,
  showCurrentTimeIndicator = true,
}: AcademyScheduleCalendarProps) {
  const scheduleScrollRef = useRef<HTMLDivElement>(null);
  const [internalWeekStart, setInternalWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const displayWeekStart = controlledWeekStart ?? internalWeekStart;
  const setDisplayWeekStart = useCallback(
    (week: Date) => {
      if (controlledWeekStart === undefined) {
        setInternalWeekStart(week);
      }
      onWeekStartChange?.(week);
    },
    [controlledWeekStart, onWeekStartChange]
  );
  const [internalScheduleView, setInternalScheduleView] = useState<'monthly' | 'weekly'>(() => defaultView ?? 'monthly');
  const academyId = lessons[0]?.academyId ?? academyIdForAgeGroups;
  const { ageGroupById, ageGroupLabelById, activeAgeGroups } = useAgeGroupLookups(academyId);

  const hasFullHeader = viewMode !== undefined && onViewModeChange !== undefined;
  const isControlled = controlledScheduleView !== undefined || hasFullHeader;
  const scheduleView = hasFullHeader
    ? (viewMode === 'monthly' || viewMode === 'weekly' ? viewMode : 'monthly')
    : isControlled
      ? controlledScheduleView!
      : internalScheduleView;
  const setScheduleView =
    hasFullHeader
      ? (v: 'monthly' | 'weekly') => onViewModeChange!(v)
      : isControlled && onScheduleViewChange
        ? onScheduleViewChange
        : setInternalScheduleView;
  const showViewToggle = !isControlled;

  /** Prefer month grid on phone when schedule view is internal-only (e.g. coach schedule). */
  useEffect(() => {
    if (isControlled) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    setInternalScheduleView((prev) => (prev === 'weekly' ? 'monthly' : prev));
  }, [isControlled]);

  const displayWeekEnd = addDays(displayWeekStart, 6);

  const occurrences = useMemo(() => {
    const lessonOccurrences = buildAcademyOccurrencesFromLessonSessions(
      lessons,
      terms,
      lessonSessions,
      makeupSessions,
      pendingScheduleBookings,
      reportedAbsenceSessions,
      courtNamesById,
      scheduleForCoachId,
      scheduleActiveStatusFilter,
      scheduleEnrollmentBookings,
      removedFromSessionEnrollments
    );
    const merged = [...lessonOccurrences, ...extraOccurrences];
    if (!sourceTypeFilters?.length) return merged;
    const allowed = new Set(sourceTypeFilters);
    return merged.filter((occ) => allowed.has(occ.sourceType));
  }, [
    lessons,
    terms,
    makeupSessions,
    pendingScheduleBookings,
    scheduleEnrollmentBookings,
    removedFromSessionEnrollments,
    reportedAbsenceSessions,
    lessonSessions,
    courtNamesById,
    scheduleForCoachId,
    scheduleActiveStatusFilter,
    extraOccurrences,
    sourceTypeFilters,
  ]);

  /** Postpone replacement + original row lookups for calendar chips */
  const lessonSessionLinkIndex = useMemo(() => {
    const byId = new Map<string, LessonSession>();
    const postponedOriginalByReplacementId = new Map<string, LessonSession>();
    for (const s of lessonSessions ?? []) {
      byId.set(s.id, s);
      if (s.newSessionId) postponedOriginalByReplacementId.set(s.newSessionId, s);
    }
    return { byId, postponedOriginalByReplacementId };
  }, [lessonSessions]);

  const scheduleOccurrenceToFullClickPayload = useCallback(
    (occ: ScheduleOccurrence) =>
      enrichLessonOccurrenceClickPayloadFully(scheduleOccurrenceToClickPayload(occ), lessonSessions),
    [lessonSessions]
  );

  const occurrencesInWeek = useMemo(() => {
    return occurrences.filter((occ) => {
      const d = occ.date;
      return d >= displayWeekStart && d <= displayWeekEnd;
    });
  }, [occurrences, displayWeekStart, displayWeekEnd]);

  const weekContainsToday = useMemo(() => {
    if (weeklyColumnHeaders !== 'calendar') return false;
    const now = new Date();
    return [0, 1, 2, 3, 4, 5, 6].some((i) => isSameDay(addDays(displayWeekStart, i), now));
  }, [displayWeekStart, weeklyColumnHeaders]);

  const showNowIndicator = showCurrentTimeIndicator && weekContainsToday;

  const { earliestTime, latestTime } = useMemo(() => {
    let earliest = 24 * 60;
    let latest = 0;
    occurrencesInWeek.forEach((occ) => {
      const s = timeToMinutes(occ.startTime);
      const e = timeToMinutes(occ.endTime);
      if (s < earliest) earliest = s;
      if (e > latest) latest = e;
    });
    if (previewLesson?.startTime && previewLesson?.endTime) {
      const ps = timeToMinutes(previewLesson.startTime);
      const pe = timeToMinutes(previewLesson.endTime);
      if (ps < earliest) earliest = ps;
      if (pe > latest) latest = pe;
    }
    if (occurrencesInWeek.length === 0 && !previewLesson) {
      const base = { earliestTime: 8 * 60, latestTime: 20 * 60 };
      return showNowIndicator ? ensureTimeRangeIncludesNow(base.earliestTime, base.latestTime) : base;
    }
    earliest = Math.max(0, Math.floor(earliest / 30) * 30 - 60);
    latest = Math.min(24 * 60, Math.ceil(latest / 30) * 30 + 60);
    return showNowIndicator
      ? ensureTimeRangeIncludesNow(earliest, latest)
      : { earliestTime: earliest, latestTime: latest };
  }, [occurrencesInWeek, previewLesson, showNowIndicator]);

  const timeSlots = useMemo(() => {
    const slots: number[] = [];
    for (let m = earliestTime; m <= latestTime; m += 30) slots.push(m);
    return slots;
  }, [earliestTime, latestTime]);

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, ScheduleOccurrence[]>();
    occurrencesInWeek.forEach((occ) => {
      const dateKey = format(occ.date, 'yyyy-MM-dd');
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(occ);
    });
    return map;
  }, [occurrencesInWeek]);

  const totalRange = latestTime - earliestTime;

  const fallbackAnchorMinutes = useMemo(() => {
    let min: number | null = null;
    occurrencesInWeek.forEach((occ) => {
      const s = timeToMinutes(occ.startTime);
      if (min === null || s < min) min = s;
    });
    if (previewLesson?.startTime) {
      const s = timeToMinutes(previewLesson.startTime);
      if (min === null || s < min) min = s;
    }
    return min;
  }, [occurrencesInWeek, previewLesson]);

  useScheduleScrollToNow({
    scrollRef: scheduleScrollRef,
    enabled: showNowIndicator,
    earliestTime,
    latestTime,
    pxPerSlot: WEEKLY_PX_PER_30_MIN,
    fallbackAnchorMinutes,
    deps: [displayWeekStart, scheduleView],
  });

  const getOccurrenceChipProps = (occ: ScheduleOccurrence): {
    className: string;
    colorStyle?: React.CSSProperties;
  } => {
    const isPast = isScheduleOccurrencePast(occ.date, occ.endTime);
    const isReplacement =
      occ.sourceType === 'lesson' &&
      !!occ.sessionId &&
      occ.status === 'scheduled' &&
      lessonSessionLinkIndex.postponedOriginalByReplacementId.has(occ.sessionId);

    const chipProps = getScheduleOccurrenceChipProps(occ, {
      ageGroupById,
      isReplacement,
    });

    return {
      className: appendSchedulePastOpacity(chipProps.className, isPast),
      colorStyle: chipProps.style,
    };
  };

  const hasLessons = lessons.length > 0;
  const hasPreview = !!previewLesson?.daysOfWeek?.length && !!previewLesson.startTime && !!previewLesson.endTime;

  const calendarToolbarStart =
    !hideHeader && hasFullHeader && (filterSlot || lessonCount != null) ? (
      <ScheduleFiltersAndCount filterSlot={filterSlot} lessonCount={lessonCount} />
    ) : undefined;

  // Full header: view toggles, filters, count, create (academy owner only)
  const Header = hasFullHeader && !hideHeader ? (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6 py-2 w-full">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'monthly' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange!('monthly')}
            className="gap-1.5 sm:gap-1.5"
          >
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Monthly</span>
          </Button>
          <Button
            variant={viewMode === 'weekly' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange!('weekly')}
            className="gap-1.5 sm:gap-1.5"
          >
            <CalendarClock className="h-4 w-4" />
            <span className="hidden sm:inline">Weekly</span>
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange!('list')}
            className="gap-1.5 sm:gap-1.5"
          >
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">List</span>
          </Button>
        </div>
        <ScheduleFiltersAndCount filterSlot={filterSlot} lessonCount={lessonCount} />
      </div>
      {showCreateLesson && onCreateLesson && (
        <div className="flex items-center gap-2">
          <Tooltip content={createLessonTooltip ?? ''} disabled={!createLessonTooltip}>
            <Button onClick={() => onCreateLesson?.()} disabled={createLessonDisabled}>
              Create Lesson
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  ) : null;

  const ViewToggleButtons = showViewToggle ? (
    <div className="flex flex-shrink-0 flex-wrap items-center gap-2" role="group" aria-label="Schedule view">
      <Button
        variant={scheduleView === 'monthly' ? 'default' : 'outline'}
        size="sm"
        onClick={() => setScheduleView('monthly')}
        className="gap-1.5"
      >
        <Calendar className="h-4 w-4" />
        <span className="hidden sm:inline">Monthly</span>
      </Button>
      <Button
        variant={scheduleView === 'weekly' ? 'default' : 'outline'}
        size="sm"
        onClick={() => setScheduleView('weekly')}
        className="gap-1.5"
      >
        <CalendarClock className="h-4 w-4" />
        <span className="hidden sm:inline">Weekly</span>
      </Button>
    </div>
  ) : null;

  // Simple mode: parent signals load in progress (e.g. coach schedule) — avoid empty state flash
  if (!hasFullHeader && loading) {
    return (
      <div className="w-full">
        {Header}
        <div className="flex min-h-[220px] flex-col items-center justify-center py-16">
          <div
            className="h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent"
            aria-hidden
          />
          <p className="mt-4 text-sm text-muted-foreground">Loading schedule…</p>
        </div>
      </div>
    );
  }

  // Full header mode: loading, empty, or list view
  if (hasFullHeader) {
    if (loading) {
      return (
        <div className="w-full">
          {Header}
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        </div>
      );
    }
    if (emptyContent && (!hasFullHeader || viewMode === 'list')) {
      if (!hideHeader && calendarToolbarStart) {
        return (
          <ScheduleListShell header={Header} title="Lessons" toolbarTrailing={calendarToolbarStart}>
            {emptyContent}
          </ScheduleListShell>
        );
      }
      return (
        <div className="w-full">
          {Header}
          {emptyContent}
        </div>
      );
    }
    if (viewMode === 'list' && listContent) {
      if (!hideHeader && calendarToolbarStart) {
        return (
          <ScheduleListShell header={Header} title="Lessons" toolbarTrailing={calendarToolbarStart}>
            {listContent}
          </ScheduleListShell>
        );
      }
      return (
        <div className="w-full">
          {Header}
          {listContent}
        </div>
      );
    }
  }

  // Simple mode: no lessons (lesson management monthly/weekly still renders the calendar grid empty)
  const showScheduleWhenNoLessons =
    hasFullHeader && (viewMode === 'monthly' || viewMode === 'weekly');
  if (!hasLessons && !hasPreview && !showScheduleWhenNoLessons) {
    return (
      <div className="w-full">
        {Header}
        {emptyContent ?? (
          <div className="overflow-hidden rounded-lg border border-gray-200 p-12 text-center dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400">No lessons yet.</p>
          </div>
        )}
      </div>
    );
  }

  const monthlyToolbarStart =
    calendarToolbarStart || scheduleToolbarEnd ? (
      <>
        {calendarToolbarStart}
        {scheduleToolbarEnd}
      </>
    ) : undefined;

  if (scheduleView === 'monthly') {
    return (
      <div className="w-full">
        {Header}
        {showViewToggle && (
          <div className="mb-4 flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
            {ViewToggleButtons}
          </div>
        )}
        {scheduleToolbarBelow && <div className="mb-4">{scheduleToolbarBelow}</div>}
        <LessonCalendarView
          lessons={lessons}
          terms={terms}
          overrides={overrides}
          lessonSessions={lessonSessions}
          makeupSessions={makeupSessions}
          pendingScheduleBookings={pendingScheduleBookings}
          scheduleEnrollmentBookings={scheduleEnrollmentBookings}
          removedFromSessionEnrollments={removedFromSessionEnrollments}
          reportedAbsenceSessions={reportedAbsenceSessions}
          courtNamesById={courtNamesById}
          scheduleForCoachId={scheduleForCoachId}
          coaches={coaches}
          currentMonth={currentMonth}
          onCurrentMonthChange={onCurrentMonthChange}
          hideMonthNav={hideHeader}
          toolbarStart={monthlyToolbarStart}
          onLessonOccurrenceClick={
            onLessonOccurrenceClick
              ? (occ) =>
                  onLessonOccurrenceClick(
                    enrichLessonOccurrenceClickPayloadFully(
                      lessonCalendarOccurrenceToClickPayload(occ),
                      lessonSessions
                    )
                  )
              : undefined
          }
          onScheduleOccurrenceClick={onScheduleOccurrenceClick}
          coachAvailabilityWarnings={coachAvailabilityWarnings}
          onMonthChange={onMonthChange}
          tooltipContext={{ ageGroupLabelById, programNameById }}
          ageGroupById={ageGroupById}
          scheduleActiveStatusFilter={scheduleActiveStatusFilter}
          showSportGlyphsOnChips={showSportGlyphsOnChips}
          academySports={academySports}
          extraOccurrences={extraOccurrences}
          sourceTypeFilters={sourceTypeFilters}
        />
        <div className="mt-3 flex shrink-0 flex-col gap-2 px-0.5 pb-0.5 sm:mt-4">
          {scheduleLegend ?? (
            <>
              <AgeGroupCalendarLegend ageGroups={activeAgeGroups} />
              <ScheduleTypeStatusLegend />
            </>
          )}
        </div>
      </div>
    );
  }

  const weeklyToolbarTrailing =
    !hideHeader && (calendarToolbarStart || ViewToggleButtons || scheduleToolbarEnd) ? (
      <>
        {calendarToolbarStart}
        {showViewToggle ? ViewToggleButtons : null}
        {scheduleToolbarEnd}
      </>
    ) : undefined;

  const weeklyGridMinHeightPx = (totalRange / 30) * WEEKLY_PX_PER_30_MIN;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="flex h-[calc(100svh-14rem)] min-h-[320px] w-full min-w-0 flex-col gap-3 overflow-hidden sm:gap-4 md:h-[calc(100svh-12.5rem)] md:min-h-[360px]">
      {Header}
      {scheduleToolbarBelow && <div className="shrink-0">{scheduleToolbarBelow}</div>}

      {!hideHeader ? (
      <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <ScheduleWeekNav
          weekStart={displayWeekStart}
          onWeekStartChange={setDisplayWeekStart}
          onWeekChange={onWeekChange}
        />
        {weeklyToolbarTrailing ? (
          <div className="flex w-full min-w-0 items-center gap-2 sm:ml-auto sm:w-auto sm:justify-end sm:gap-3">
            {weeklyToolbarTrailing}
          </div>
        ) : null}
      </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border">
        {/*
          Fill remaining main-panel height. Inner grid uses flex-1 so a sparse week
          (few lessons / short time range) still stretches; minHeight keeps dense weeks scrollable.
        */}
        <div
          ref={scheduleScrollRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain [scrollbar-gutter:stable]"
        >
          <div className="flex min-h-full min-w-[560px] flex-col">
          <div
            className={`${WEEKLY_GRID_CLASS} sticky top-0 z-30 shrink-0 border-b border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800`}
          >
            <div className="border-r border-gray-200 bg-gray-100 px-0.5 py-1.5 dark:border-gray-700 dark:bg-gray-800 md:px-1 md:py-2">
              <div className="text-center text-[10px] font-semibold leading-tight text-gray-900 dark:text-white md:text-xs">
                Time
              </div>
            </div>
            {[0, 1, 2, 3, 4, 5, 6].map((colIndex) => {
              const dayDate = addDays(displayWeekStart, colIndex);
              const isToday =
                weeklyColumnHeaders === 'calendar' && isSameDay(dayDate, new Date());
              return (
                <div
                  key={colIndex}
                  className={`border-r border-gray-200 p-1 text-center last:border-r-0 dark:border-gray-700 md:p-2 ${
                    isToday
                      ? 'bg-primary/10 ring-2 ring-inset ring-primary/40 dark:bg-primary/15'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}
                  aria-current={isToday ? 'date' : undefined}
                >
                  <div className="text-xs font-semibold text-gray-900 dark:text-white md:text-sm">
                    {DAYS_SHORT[dayDate.getDay()]}
                  </div>
                  {weeklyColumnHeaders === 'calendar' ? (
                    <div
                      className={`text-[10px] md:text-xs ${
                        isToday ? 'font-semibold text-primary' : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {format(dayDate, 'MMM d')}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div
            className={`${WEEKLY_GRID_CLASS} relative min-h-0 flex-1`}
            style={{ minHeight: `${weeklyGridMinHeightPx}px` }}
          >
            {showNowIndicator ? (
              <div className="pointer-events-none absolute inset-0 z-30" aria-hidden>
                <ScheduleCurrentTimeIndicator
                  earliestTime={earliestTime}
                  latestTime={latestTime}
                  showDot
                />
              </div>
            ) : null}
            <div className="relative sticky left-0 z-20 border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
              {timeSlots.filter((_, i) => i % 2 === 0).map((slotM) => {
                const pos = ((slotM - earliestTime) / totalRange) * 100;
                const h = Math.floor(slotM / 60);
                const m = slotM % 60;
                const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                return (
                  <div
                    key={slotM}
                    className="absolute left-0 right-0 px-0.5 text-[10px] leading-none text-gray-600 dark:text-gray-400 text-right tabular-nums"
                    style={{ top: `${pos}%` }}
                  >
                    {formatTime(timeStr)}
                  </div>
                );
              })}
            </div>

          {[0, 1, 2, 3, 4, 5, 6].map((colIndex) => {
            const dayDate = addDays(displayWeekStart, colIndex);
            const dateKey = format(dayDate, 'yyyy-MM-dd');
            const dayOccurrences = occurrencesByDate.get(dateKey) ?? [];
            const isToday =
              weeklyColumnHeaders === 'calendar' && isSameDay(dayDate, new Date());

            return (
              <div
                key={colIndex}
                className={`border-r border-gray-200 last:border-r-0 relative dark:border-gray-700 ${
                  isToday ? 'bg-primary/5 dark:bg-primary/10' : 'bg-white dark:bg-gray-900'
                }`}
              >
                {timeSlots.map((slotM) => {
                  const pos = ((slotM - earliestTime) / totalRange) * 100;
                  return (
                    <div
                      key={slotM}
                      className="absolute left-0 right-0 border-b border-gray-100 dark:border-gray-800"
                      style={{ top: `${pos}%` }}
                    />
                  );
                })}

                {hasPreview &&
                  previewLesson &&
                  previewLesson.daysOfWeek.includes(dayDate.getDay()) && (
                    <div
                      className="absolute rounded p-1.5 text-left text-xs font-medium border-2 border-dashed border-blue-400 dark:border-blue-500 bg-blue-50/70 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 z-10 opacity-90 overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight"
                      style={(() => {
                        const previewSlot = {
                          id: PREVIEW_STACK_ID,
                          startTime: previewLesson.startTime!,
                          endTime: previewLesson.endTime!,
                        };
                        const stackWithPreview = [
                          ...dayOccurrences.map((o) => ({
                            id: scheduleOccurrenceStackKey(o),
                            startTime: o.startTime,
                            endTime: o.endTime,
                          })),
                          previewSlot,
                        ];
                        return getStackedTimeSlotStyle(
                          previewSlot,
                          stackWithPreview,
                          (s) => s.id,
                          earliestTime,
                          latestTime
                        ) as React.CSSProperties;
                      })()}
                    >
                      <span className="truncate font-medium min-w-0">
                        {previewLesson.name || 'New lesson'}
                      </span>
                      <div className="truncate opacity-90 text-[10px] min-w-0">
                        {formatTimeHHMM(previewLesson.startTime)} –{' '}
                        {formatTimeHHMM(previewLesson.endTime)}
                      </div>
                      <span className="inline-block mt-0.5 px-1 py-0 rounded text-[10px] bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 self-start max-w-full truncate">
                        Preview
                      </span>
                    </div>
                  )}
                {dayOccurrences.map((occ) => {
                  const style = getStackedTimeSlotStyle(
                    occ,
                    dayOccurrences,
                    scheduleOccurrenceStackKey,
                    earliestTime,
                    latestTime
                  );
                  const isClickable =
                    (occ.sourceType === 'lesson' && !!onLessonOccurrenceClick) ||
                    (occ.sourceType !== 'lesson' && !!onScheduleOccurrenceClick);
                  const occKey = `${occ.sourceType}-${occ.lessonId}-${occ.date.getTime()}`;
                  const showPrivateLinkTooltip = occ.isPrivate && onCopyPrivateLessonLink;
                  const postponedRow =
                    occ.sessionId && occ.status === 'postponed'
                      ? lessonSessionLinkIndex.byId.get(occ.sessionId)
                      : undefined;
                  const replacementSession =
                    postponedRow?.newSessionId != null
                      ? lessonSessionLinkIndex.byId.get(postponedRow.newSessionId)
                      : undefined;
                  const originalOfPostponed =
                    occ.sessionId && occ.status === 'scheduled'
                      ? lessonSessionLinkIndex.postponedOriginalByReplacementId.get(occ.sessionId)
                      : undefined;

                  const chipProps = getOccurrenceChipProps(occ);

                  const occurrenceCell = (
                    <div
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onClick={
                        isClickable
                          ? () => {
                              if (occ.sourceType === 'lesson' && onLessonOccurrenceClick) {
                                onLessonOccurrenceClick(scheduleOccurrenceToFullClickPayload(occ));
                              } else if (onScheduleOccurrenceClick) {
                                onScheduleOccurrenceClick(occ);
                              }
                            }
                          : undefined
                      }
                      onKeyDown={
                        isClickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (occ.sourceType === 'lesson' && onLessonOccurrenceClick) {
                                  onLessonOccurrenceClick(scheduleOccurrenceToFullClickPayload(occ));
                                } else if (onScheduleOccurrenceClick) {
                                  onScheduleOccurrenceClick(occ);
                                }
                              }
                            }
                          : undefined
                      }
                      className={`absolute rounded p-1.5 text-left text-xs font-medium border transition-all z-10 overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight ${chipProps.className} ${isClickable ? 'cursor-pointer hover:shadow-md' : ''}`}
                      style={{
                        top: style.top,
                        height: style.height,
                        left: style.left,
                        width: style.width,
                        ...chipProps.colorStyle,
                      }}
                    >
                      <div className="flex items-center gap-1 min-w-0 shrink-0">
                        {occ.status === 'cancelled' && (
                          <XCircle className="h-3 w-3 flex-shrink-0" />
                        )}
                        <span className="truncate font-medium min-w-0 flex-1">{occ.lessonName}</span>
                        {showSportGlyphsOnChips ? (
                          <CalendarChipSportGlyph
                            lesson={occ.lesson}
                            academySports={academySports}
                            enabled={showSportGlyphsOnChips}
                            variant="dark"
                          />
                        ) : null}
                      </div>
                      <div className="truncate opacity-90 text-[10px] min-w-0 shrink-0">
                        {formatTimeHHMM(occ.startTime)} – {formatTimeHHMM(occ.endTime)}
                      </div>
                      {occ.courtLabel && (
                        <div className="truncate opacity-90 text-[10px] min-w-0 shrink-0">{occ.courtLabel}</div>
                      )}
                      {occ.skillLevel && (
                        <span className="text-[10px] opacity-75 truncate min-w-0 shrink-0">· {occ.skillLevel}</span>
                      )}
                      {occ.maxCapacity !== -1 && (
                        <div className="flex items-center gap-1 mt-0.5 text-[10px] min-w-0 shrink-0">
                          <Users className="h-2.5 w-2.5 flex-shrink-0" />
                          <span className="min-w-0 flex-1 truncate">
                            {occ.enrollmentCount}/{occ.maxCapacity} students
                          </span>
                          {occ.makeupCount && occ.makeupCount > 0 && (
                            <Tooltip
                              content={
                                occ.makeupCount === 1
                                  ? '1 make-up student attending this session'
                                  : `${occ.makeupCount} make-up students attending this session`
                              }
                              side="top"
                            >
                              <span
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-medium bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 ml-0.5 cursor-help"
                              >
                                +{occ.makeupCount}
                              </span>
                            </Tooltip>
                          )}
                          {occ.pendingEnrollmentCount && occ.pendingEnrollmentCount > 0 && (
                            <Tooltip
                              content={
                                occ.pendingEnrollmentCount === 1
                                  ? '1 booking awaiting confirmation or payment'
                                  : `${occ.pendingEnrollmentCount} bookings awaiting confirmation or payment`
                              }
                              side="top"
                            >
                              <span
                                className="inline-flex items-center justify-center min-w-[1rem] h-4 px-0.5 rounded-full text-[9px] font-medium bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 ml-0.5 cursor-help"
                              >
                                !{occ.pendingEnrollmentCount}
                              </span>
                            </Tooltip>
                          )}
                          {occ.reportedAbsenceCount && occ.reportedAbsenceCount > 0 && (
                            <Tooltip
                              content={
                                occ.reportedAbsenceCount === 1
                                  ? '1 student reported absent for this session'
                                  : `${occ.reportedAbsenceCount} students reported absent for this session`
                              }
                              side="top"
                            >
                              <span
                                className="inline-flex items-center justify-center min-w-[1rem] h-4 px-0.5 rounded-full text-[9px] font-medium bg-slate-200 dark:bg-slate-600 text-slate-900 dark:text-slate-100 ml-0.5 cursor-help"
                              >
                                −{occ.reportedAbsenceCount}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      )}
                      {occ.maxCapacity === -1 && occ.makeupCount && occ.makeupCount > 0 && (
                        <div className="flex items-center gap-1 mt-0.5 text-[10px] min-w-0 shrink-0">
                          <Tooltip
                            content={
                              occ.makeupCount === 1
                                ? '1 make-up student attending this session'
                                : `${occ.makeupCount} make-up students attending this session`
                            }
                            side="top"
                          >
                            <span
                              className="inline-flex items-center justify-center min-w-[1rem] h-4 px-0.5 rounded-full text-[9px] font-medium bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 cursor-help max-w-full truncate"
                            >
                              +{occ.makeupCount} make-up
                            </span>
                          </Tooltip>
                        </div>
                      )}
                      {occ.maxCapacity === -1 &&
                        occ.pendingEnrollmentCount &&
                        occ.pendingEnrollmentCount > 0 && (
                          <div className="flex items-center gap-1 mt-0.5 text-[10px] min-w-0 shrink-0">
                            <Tooltip
                              content={
                                occ.pendingEnrollmentCount === 1
                                  ? '1 booking awaiting confirmation or payment'
                                  : `${occ.pendingEnrollmentCount} bookings awaiting confirmation or payment`
                              }
                              side="top"
                            >
                              <span
                                className="inline-flex items-center justify-center min-w-[1rem] h-4 px-0.5 rounded-full text-[9px] font-medium bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 cursor-help max-w-full truncate"
                              >
                                !{occ.pendingEnrollmentCount} unconfirmed
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      {occ.maxCapacity === -1 &&
                        occ.reportedAbsenceCount &&
                        occ.reportedAbsenceCount > 0 && (
                          <div className="flex items-center gap-1 mt-0.5 text-[10px] min-w-0 shrink-0">
                            <Tooltip
                              content={
                                occ.reportedAbsenceCount === 1
                                  ? '1 student reported absent for this session'
                                  : `${occ.reportedAbsenceCount} students reported absent for this session`
                              }
                              side="top"
                            >
                              <span
                                className="inline-flex items-center justify-center min-w-[1rem] h-4 px-0.5 rounded-full text-[9px] font-medium bg-slate-200 dark:bg-slate-600 text-slate-900 dark:text-slate-100 cursor-help max-w-full truncate"
                              >
                                −{occ.reportedAbsenceCount} absent
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      {occ.isPrivate && (
                        <span className="inline-block mt-0.5 px-1 py-0 rounded text-[10px] bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200 self-start max-w-full truncate">
                          Private
                        </span>
                      )}
                      {occ.isCamp && (
                        <span className="inline-block mt-0.5 px-1 py-0 rounded text-[10px] bg-teal-200 dark:bg-teal-800 text-teal-900 dark:text-teal-100 self-start max-w-full truncate">
                          Camp
                        </span>
                      )}
                      {occ.status === 'postponed' && (
                        <span className="inline-block mt-0.5 px-1 py-0 rounded text-[10px] bg-rose-200 dark:bg-rose-800 text-rose-900 dark:text-rose-100 self-start max-w-full truncate">
                          Postponed
                        </span>
                      )}
                      {replacementSession && (
                        <span
                          className="inline-block mt-0.5 px-1 py-0 rounded text-[9px] bg-rose-700/15 dark:bg-rose-800/40 text-rose-950 dark:text-rose-100 self-start max-w-full truncate"
                          title="Replacement session (lesson_sessions)"
                        >
                          New:{' '}
                          {format(
                            parse(
                              replacementSession.sessionDate.replace(/T.*/, '').slice(0, 10),
                              'yyyy-MM-dd',
                              new Date()
                            ),
                            'EEE MMM d'
                          )}{' '}
                          {formatTimeHHMM(replacementSession.startTime)}
                        </span>
                      )}
                      {originalOfPostponed && (
                        <span
                          className="inline-block mt-0.5 px-1 py-0 rounded text-[10px] bg-indigo-200 dark:bg-indigo-900/50 text-indigo-900 dark:text-indigo-100 self-start max-w-full truncate"
                          title="This session replaces a postponed occurrence"
                        >
                          Rescheduled
                        </span>
                      )}
                      {occ.makeupCount && occ.makeupCount > 0 && (
                        <span
                          className="inline-block mt-0.5 px-1 py-0 rounded text-[10px] font-medium bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-100 self-start max-w-full truncate"
                          title="At least one student attending with a make-up credit"
                        >
                          Make-up
                        </span>
                      )}
                    </div>
                  );

                  if (showPrivateLinkTooltip) {
                    return (
                      <TooltipRoot key={occKey}>
                        <TooltipTrigger asChild>{occurrenceCell}</TooltipTrigger>
                        <TooltipContent
                          side="top"
                          sideOffset={8}
                          className="max-w-[min(280px,90vw)] border-0 bg-gray-900 p-3 text-sm text-white shadow-xl dark:bg-gray-800"
                        >
                          <p className="mb-2 font-semibold leading-snug">{occ.lessonName}</p>
                          <p className="mb-3 text-xs text-gray-300">
                            Share the public booking link for this private lesson.
                          </p>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="w-full gap-2 text-gray-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCopyPrivateLessonLink!(occ.lessonId, occ.lessonName);
                            }}
                          >
                            <Link2 className="h-4 w-4 shrink-0" />
                            Copy link
                          </Button>
                        </TooltipContent>
                      </TooltipRoot>
                    );
                  }

                  return (
                    <React.Fragment key={occKey}>{occurrenceCell}</React.Fragment>
                  );
                })}
              </div>
            );
          })}
          </div>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-2 px-0.5 pb-0.5">
        {scheduleLegend ?? (
          <>
            <AgeGroupCalendarLegend ageGroups={activeAgeGroups} />
            <ScheduleTypeStatusLegend />
          </>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}
